import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import LinkItem from './LinkItem.jsx'
import { WindowLink } from './WindowSubmenu.jsx'
import { useContext } from 'hooks/useURL'
import { compatibleWithCurrentScreenType } from 'Helper/screenTypeHelpers'

const FavoritesSubmenu = ({ windows, screens, workflows, inWorkflow, prefix }) => {
  const interactableWindows = useSelector(state => state.interactableState.windows) || {}
  const favorites = (useSelector(state => state.userConfig.favorites) || []).filter(compatibleWithCurrentScreenType)
  const context = useContext()

  const favoriteWindows = useMemo(() => {
    const favWindows = favorites.filter(fav => fav.name === `${context}-window`)
    // possible windows are the configured windows but also the windows available in any screens
    const possibleWindows = [...windows]
    screens.forEach(screen => {
      (screen.windows || []).forEach(win => {
        if (possibleWindows.find(possibleWin => possibleWin.ref === win.ref) == null) {
          possibleWindows.push(win)
        }
      })
    })

    return possibleWindows.filter(win => favWindows.find(fav => fav.value === win.ref) != null)
      .sort((a, b) => {
        const textA = a.name ? a.name.toUpperCase() : ''
        const textB = b.name ? b.name.toUpperCase() : ''
        return textA < textB ? -1 : textA > textB ? 1 : 0
      }).map((window, idx) => {
        return (
          <WindowLink
            key={idx}
            window={window}
            inWorkflow={inWorkflow}
            interactableWindows={interactableWindows}
          />
        )
      })
  }, [windows, favorites, interactableWindows, inWorkflow, context])

  const favoriteScreens = useMemo(() => {
    const favScreens = favorites.filter(fav => fav.name === `${context}-screen`)
    return screens.filter(screen => favScreens.find(fav => fav.value === screen.path) != null)
      .sort((a, b) => {
        const textA = a.name ? a.name.toUpperCase() : ''
        const textB = b.name ? b.name.toUpperCase() : ''
        return textA < textB ? -1 : textA > textB ? 1 : 0
      }).map((screen, idx) => {
        return (
          <LinkItem
            key={idx}
            item={screen}
            type="screen"
            prefix={prefix}
            context={context}
          />
        )
      })
  }, [screens, favorites, prefix])

  const favoriteWorkflows = useMemo(() => {
    const favWorkflows = favorites.filter(fav => fav.name === `${context}-workflow`)
    return workflows.filter(workflow => favWorkflows.find(fav => fav.value === workflow.path) != null)
      .sort((a, b) => {
        const textA = a.name ? a.name.toUpperCase() : ''
        const textB = b.name ? b.name.toUpperCase() : ''
        return textA < textB ? -1 : textA > textB ? 1 : 0
      }).map((workflow, idx) => {
        return (
          <LinkItem
            key={idx}
            type="workflow"
            item={workflow}
            prefix={prefix}
            context={context}
          />
        )
      })
  }, [workflows, favorites])

  if (favoriteWindows.length === 0 && favoriteScreens.length === 0 && favoriteWorkflows.length === 0) {
    return null
  } else {
    return (
      <li className="dropdown-submenu">
        <a
          className="dropdown-toggle"
          id="favoritesDropDown"
          role="button"
          data-toggle="dropdown"
          aria-haspopup="true"
          aria-expanded="true"
          aria-labelledby="favorites menu"
        >
          <FontAwesomeIcon icon={['fas', 'star']} /> Favorites
        </a>
        <ul
          className="dropdown-menu dropdown-menu-right"
          aria-labelledby="favoritesDropDown"
        >
        {
          favoriteWindows.length > 0
            ? favoriteWindows
            : null
        }
        {
          favoriteWindows.length > 0 && favoriteScreens.length > 0
            ? <li className="divider" />
            : null
        }
        {
          favoriteScreens.length > 0
            ? favoriteScreens
            : null
        }
        {
          favoriteScreens.length > 0 && favoriteWorkflows.length > 0 ||
          favoriteWorkflows.length > 0 && favoriteScreens.length === 0 && favoriteWindows.length > 0
            ? <li className="divider" />
            : null
        }
        {
          favoriteWorkflows.length > 0
            ? favoriteWorkflows
            : null
        }
        </ul>
      </li>
    )
  }
}

FavoritesSubmenu.propTypes = {
  // An array of window objects for the window menu
  windows: PropTypes.arrayOf(
    PropTypes.shape({
      // Width to display window as percentage of screen
      width: PropTypes.string,
      // Height to display window as percentage of screen
      height: PropTypes.string,
      // X position of the upper left window corner as percentage of the screen
      x: PropTypes.string,
      // Y position of the upper left window corner as a percentage of the screen
      y: PropTypes.string,
      // User visible name of the window
      name: PropTypes.string,
      // the ref attached to the window component within the WindowContainer component
      ref: PropTypes.string.isRequired,
      // Should the window be visible in the window menu
      selectable: PropTypes.bool,
      inWorkflow: PropTypes.bool
    }).isRequired
  ),
  // An array of screen objects
  screens: PropTypes.arrayOf(
    PropTypes.shape({
      // User visible screen name
      name: PropTypes.string.isRequired,
      // Screen name used in the url
      path: PropTypes.string.isRequired,
      // Should the screen be listed in the screen menu
      selectable: PropTypes.bool.isRequired,
      // Array of windows in the screen
      windows: PropTypes.array.isRequired
    })
  ),
  workflows: PropTypes.arrayOf(
    PropTypes.shape({
      // User visible workflow name
      name: PropTypes.string.isRequired,
      // Workflow name used in the url
      path: PropTypes.string.isRequired,
      // Should the workflow be listed in the workflow menu
      selectable: PropTypes.bool.isRequired,
      // Array of screens in the workflow
      sequence: PropTypes.array.isRequired
    })
  )
}

export default FavoritesSubmenu
